<template>
    <a-modal
        :visible="true"
        width="80vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="rpn-wrap"
        @cancel="closeDialog"
    >
        <div class="rpn-modal" ref="root">
            <div class="rpn-head">
                <div>
                    <h3>Adicionar produtos</h3>
                    <div class="rpn-sub">Requisição #{{ quotation }}</div>
                </div>
                <button class="rpn-x" @click="closeDialog">
                    <span class="material-icons-outlined">close</span>
                </button>
            </div>

            <div class="rpn-toolbar">
                <a-input
                    v-model="product_name_filter"
                    class="rpn-search"
                    placeholder="Digite o nome do produto"
                    allow-clear
                    @input="searchProduct"
                    @pressEnter="searchProduct"
                >
                    <a-icon slot="prefix" type="search" />
                </a-input>
                <a-button icon="plus" @click="showNewProductModal = true">Novo produto</a-button>
            </div>

            <div class="rpn-body">
                <a-table
                    size="small"
                    row-key="pro_id"
                    :columns="columns"
                    :data-source="my_products"
                    :loading="loading"
                    :pagination="false"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Nenhum produto encontrado' }"
                >
                    <template slot="wrap" slot-scope="text, item">
                        {{ item.pro_generico == 1 ? item.pro_unidade_compra : item.pro_embalagem }}
                    </template>
                    <template slot="stock" slot-scope="text, item">
                        <a-icon v-if="giroLoading" type="loading" />
                        <span v-else-if="giroRow(item)">{{ formatGiroNumber(giroRow(item).stock) }}</span>
                        <span v-else class="rpn-empty">—</span>
                    </template>
                    <template slot="giro" slot-scope="text, item">
                        <a-icon v-if="giroLoading" type="loading" />
                        <span v-else-if="giroRow(item)">{{ formatGiroNumber(giroRow(item).giro) }}</span>
                        <span v-else class="rpn-empty">—</span>
                    </template>
                    <template slot="suggestion" slot-scope="text, item">
                        <a-icon v-if="giroLoading" type="loading" />
                        <a-tooltip
                            v-else-if="giroRow(item)"
                            :title="`Cobertura prevista: ${formatGiroNumber(giroRow(item).coverage)} dias`"
                        >
                            <span class="rpn-suggestion">{{ giroSuggestion(item) }}</span>
                        </a-tooltip>
                        <a-tooltip v-else :title="giroEmptyReason(item)">
                            <span class="rpn-empty">—</span>
                        </a-tooltip>
                    </template>
                    <template slot="amount" slot-scope="text, item, index">
                        <div class="rpn-amount">
                            <a-input
                                :value="item.new_amount"
                                class="rpn-amount-input"
                                :class="{ 'rpn-amount-saved': item.saved }"
                                inputmode="numeric"
                                placeholder="0"
                                data-nav="new-amount"
                                :data-idx="index"
                                @input="handleAmountInput($event, item)"
                                @keydown="handleAmountKeydown($event, item)"
                                @blur="add_product(item)"
                            />
                            <a-icon v-if="item.saved" type="check-circle" theme="filled" class="rpn-saved-icon" />
                        </div>
                    </template>
                </a-table>
            </div>

            <div class="rpn-foot">
                <a-pagination
                    v-if="total_items > items_by_page_value"
                    size="small"
                    :current="page"
                    :total="total_items"
                    :page-size="items_by_page_value"
                    @change="update_page"
                />
                <span v-else />
                <a-button @click="closeDialog">Fechar</a-button>
            </div>
        </div>

        <new-product-modal-v2
            v-if="showNewProductModal"
            :product="new_product || {}"
            :closeDialog="closeNewProductModal"
            @emit_product="() => {}"
            @reload_products="getProducts"
            @close="closeNewProductModal"
        />
    </a-modal>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import RequestsService from "@/services/v2/requests.service";
import { typingRemoteRequestControlMixin } from "@/mixins/typing-remote-request-controls.mixin";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import NewProductModalV2 from "@/modals/new-product/new-product-v2.modal";
import { perm_mixin } from "@/mixins/permission.mixin";
import MiraiService, { buildSuggestionAlgoritmo, suggestionCodeKey, suggestionQuantity } from "@/services/MiraiService";

export default {
    name: "ResupplyProductsNewModalV2",
    components: {
        "new-product-modal-v2": NewProductModalV2,
    },
    mixins: [typingRemoteRequestControlMixin, loaderMixin, perm_mixin],
    props: ["quotation", "closeDialog", "item", "storeNumber", "clientId", "suggestionAlgorithm"],
    data() {
        return {
            products: [],
            savedProducts: [],
            loading: true,
            new_product: null,
            showNewProductModal: false,
            page: 1,
            my_products: [],
            my_categories: [{ value: undefined, text: "TODOS" }],
            product_name_filter: null,
            total_items: null,
            items_by_page_value: 30,
            svc: new RequestsService(),
            searchTime: undefined,
            service: new QuotationService(),
            giroByCode: {},
            giroLoading: false,
            giroFailed: false,
            giroRequest: 0,
            hasGiro: false,
        };
    },
    computed: {
        giroAlgoritmo() {
            return buildSuggestionAlgoritmo(this.suggestionAlgorithm || {});
        },
        columns() {
            const columns = [
                { title: "Código", dataIndex: "pro_ean", key: "pro_ean", width: 140 },
                { title: "Código interno", dataIndex: "pro_codInterno", key: "pro_codInterno", width: 130 },
                { title: "Nome", dataIndex: "pro_descricao", key: "pro_descricao" },
                { title: "Embalagem", key: "wrap", width: 110, scopedSlots: { customRender: "wrap" } },
            ];
            if (this.storeNumber) {
                columns.push({ title: "Estoque", key: "stock", width: 90, align: "center", scopedSlots: { customRender: "stock" } });
                if (this.hasGiro) {
                    columns.push({ title: "Giro (un/dia)", key: "giro", width: 110, align: "center", scopedSlots: { customRender: "giro" } });
                }
                columns.push({ title: "Sugestão", key: "suggestion", width: 90, align: "center", scopedSlots: { customRender: "suggestion" } });
            }
            columns.push({ title: "Quantidade", key: "amount", width: 150, align: "center", scopedSlots: { customRender: "amount" } });
            return columns;
        },
    },
    mounted() {
        this.getProducts();
        this.load_my_products();
    },
    methods: {
        handleAmountInput(event, item) {
            const value = event.target.value.replace(/\D/g, "");
            this.$set(item, "new_amount", value === "" ? "" : Number(value));
            event.target.value = value;
        },
        amountInputs() {
            const root = this.$refs.root;
            if (!root) return [];
            return Array.from(root.querySelectorAll('input[data-nav="new-amount"]')).sort(
                (a, b) => Number(a.dataset.idx) - Number(b.dataset.idx)
            );
        },
        focusAmountInput(input) {
            if (input) {
                input.focus();
                input.select();
            }
        },
        handleAmountKeydown(event, item) {
            const inputs = this.amountInputs();
            const index = inputs.indexOf(event.target);
            if (index === -1) return;

            const next = inputs[index + 1 === inputs.length ? 0 : index + 1];
            const previous = inputs[index === 0 ? inputs.length - 1 : index - 1];

            if (event.key === "ArrowUp") {
                event.preventDefault();
                this.add_product(item).then(() => this.focusAmountInput(previous));
            } else if (event.key === "ArrowDown" || event.key === "Tab" || event.key === "Enter") {
                event.preventDefault();
                this.add_product(item).then(() => this.focusAmountInput(next));
            }
        },
        add_product(prod) {
            const data = {
                quantidade: prod.new_amount,
                prod_id: prod.pro_id,
                flag_generico: prod.pro_generico,
            };

            if (isNaN(prod.new_amount) || prod.new_amount == prod.quantidade) return Promise.resolve();

            return this.svc.add_product_to_resupply_request(this.quotation, data).then(() => {
                this.$store.dispatch("notification/add", {
                    type: "success",
                    message: "Produto adicionado com sucesso!",
                });
                this.$set(prod, "saved", true);
                this.$set(prod, "quantidade", prod.new_amount);
                this.savedProducts.push(prod);
                this.getProducts();
            });
        },
        update_page(page) {
            this.page = page;
            this.load_my_products();
        },
        getProducts() {
            return this.service
                .getProductsResupply(this.quotation)
                .then((response) => response.data)
                .then((data) => {
                    this.loading = false;
                    this.products = data.produtos;
                    this.products.forEach((p) => {
                        p.new_amount = p.quantidade;
                        this.savedProducts.forEach((element) => {
                            if (element.pro_id == p.pro_id) p.saved = true;
                        });
                    });
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        searchProduct() {
            const time = new Date();
            this.searchTime = time;
            setTimeout(() => {
                if (this.searchTime == time) {
                    this.page = 1;
                    this.load_my_products();
                }
            }, 500);
        },
        load_my_products() {
            this.loading = true;

            this.cancelSearch(() => {
                this.loading = true;
            });
            const params = {
                nomeproduto: this.product_name_filter,
                page: this.page,
                deleted: 0,
            };
            this.service
                .getProdutosClientePorPagina(params, this.cancelSource?.token)
                .then((data) => {
                    this.my_products = data.data.produtos.data;
                    data.data.categorias.forEach((element) => {
                        if (element.cat_excluido != "1" && element.cat_status != "0")
                            this.my_categories.push({ value: element, text: element.cat_descricao });
                    });

                    this.page = data.data.produtos.current_page;
                    this.total_items = data.data.produtos.total;
                    this.products.forEach((v) => (v.expanded = false));
                    this.loading = false;
                    this.loadGiro();
                })
                .catch((error) => {
                    this.loading = false;
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        closeNewProductModal() {
            this.showNewProductModal = false;
            this.new_product = null;
        },
        async loadGiro() {
            if (!this.storeNumber) return;

            const request = ++this.giroRequest;
            const codes = this.my_products
                .map((product) => suggestionCodeKey(product.pro_codInterno))
                .filter((code) => code !== "");

            this.giroByCode = {};
            this.giroFailed = false;

            if (!codes.length) {
                this.giroLoading = false;
                return;
            }

            this.giroLoading = true;

            try {
                const response = await new MiraiService(this.clientId).suggestion({
                    storeNumber: String(this.storeNumber),
                    internalCodes: codes,
                    algoritmo: this.giroAlgoritmo,
                });
                if (request !== this.giroRequest) return;

                const byCode = {};
                (response.data.data || []).forEach((row) => {
                    byCode[suggestionCodeKey(row.internal_code)] = row;
                    if ("giro" in row) this.hasGiro = true;
                });
                this.giroByCode = byCode;
            } catch (error) {
                if (request === this.giroRequest) this.giroFailed = true;
            } finally {
                if (request === this.giroRequest) this.giroLoading = false;
            }
        },
        giroRow(item) {
            const code = suggestionCodeKey(item.pro_codInterno);
            return code ? this.giroByCode[code] || null : null;
        },
        giroSuggestion(item) {
            return this.formatGiroNumber(suggestionQuantity(item, this.giroRow(item), this.giroAlgoritmo));
        },
        giroEmptyReason(item) {
            if (!suggestionCodeKey(item.pro_codInterno)) return "Produto sem código interno";
            if (this.giroFailed) return "Não foi possível carregar o giro";
            return "Sem dados de giro nesta loja";
        },
        formatGiroNumber(value) {
            if (value === null || value === undefined || value === "") return "—";
            const num = Number(value);
            return isNaN(num) ? "—" : num.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
        },
    },
};
</script>

<style lang="scss" scoped>
.rpn-modal {
    height: 82vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.rpn-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        margin: 0;
    }
}
.rpn-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.rpn-x {
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #f0f0f0;
        color: #262626;
    }
}
.rpn-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    border-top: 1px solid #f0f0f0;
}
.rpn-search {
    flex: 1;
    max-width: 480px;
}
.rpn-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 20px 8px;
}
.rpn-amount {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.rpn-amount-input {
    width: 90px;
    text-align: center;
}
.rpn-amount-saved {
    border-color: #52c41a;
}
.rpn-saved-icon {
    color: #52c41a;
}
.rpn-empty {
    color: #bfbfbf;
}
.rpn-suggestion {
    font-weight: 600;
    cursor: default;
}
.rpn-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.rpn-wrap {
    .ant-modal {
        min-width: 640px;
        max-width: 1200px;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
